import { useEffect, useState } from "react";

import {
  Autocomplete,
  SelectData,
  Select,
  TextArea,
  TextField,
  Typography,
  TimePicker,
  InfoPanel,
} from "@king-ict/eupisi-ui/components";
import { CodeBook, EnrollmentTypeEnum } from "@king-ict/eupisi-ui/models";
import { hrShortTime, nameof } from "@king-ict/eupisi-ui/utils";
import { decimalToMinutes } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  createLocationProgramData,
  getLocationProgramData,
  getLocations,
  getWorkOrganizations,
  updateLocationProgramData,
  updateLocationProgramDetails,
  isPedagogicalYearInstitutionActive as isPedagogicalYearInstitutionActiveApi,
  getExchangeRateEurToHrk,
} from "../../../api";
import { subsequentCapacitiesRoute, useTypedSelector } from "../../../app";
import { AdministrationCreateUpdate } from "../../../components";
import { useBusinessRule } from "../../../hooks";
import {
  Location,
  LocationProgram,
  LocationProgramData,
  LocationProgramDataValidationSchema,
  WorkOrganizationEnum,
} from "../../../models";
import styles from "./SubsequentEnrollmentLocationProgramCreateUpdate.module.scss";

export const SubsequentEnrollmentLocationProgramCreateUpdate = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [workOrganizations, setWorkOrganizations] = useState<CodeBook[]>([]);
  const [
    locationProgramData,
    setLocationProgramData,
  ] = useState<LocationProgramData>();

  const [exchangeRate, setExchangeRate] = useState<number>(0);

  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const pedagogicalYear = useTypedSelector(
    (s) => s.application.pedagogicalYear
  );

  const importantDate = useTypedSelector((s) => s.application.importantDate);

  const [isPedagogicalYearInstitutionActive, setIsPedagogicalYearInstitutionActive] = useState(false);

  useEffect(() => {
    (async () => {
      setLocations(await getLocations());
      setWorkOrganizations(await getWorkOrganizations());
      setExchangeRate(await getExchangeRateEurToHrk());

      if (id) {
        const res = await getLocationProgramData(+id).then((res) => {
          return {
            ...res,
            firstShiftFrom: new Date(res.firstShiftFrom),
            secondShiftFrom: new Date(res.secondShiftFrom),
            enrollmentTypeId: EnrollmentTypeEnum.SubsequentEnrollment
          };
        });
        setLocationProgramData(res);
      } else {
        setLocationProgramData({
          firstShiftFrom: new Date(1970, 1, 1, 6, 30),
          secondShiftFrom: new Date(1970, 1, 1, 14, 30),
          enrollmentTypeId: EnrollmentTypeEnum.SubsequentEnrollment,
          monthId: importantDate.monthId
        } as LocationProgramData);
      }
    })();
  }, [id, importantDate.monthId]);

  useEffect(() => {
    (async () => {      
          await isPedagogicalYearInstitutionActiveApi()
          .then((result => {
            setIsPedagogicalYearInstitutionActive(result);
          }));
    })();
  }, [pedagogicalYear]);

  const {
    canCreateLocationProgramData,
    canUpdateLocationProgramData,
    canUpdateLocationProgramDetails,
  } = useBusinessRule();

  const canEditOrUpdate = () => {
    return (
      canCreateLocationProgramData(
        pedagogicalYear?.id,
        EnrollmentTypeEnum.SubsequentEnrollment
      ) ||
      canUpdateLocationProgramData(
        pedagogicalYear?.id,
        EnrollmentTypeEnum.SubsequentEnrollment,
        locationProgramData?.monthId
      ) ||
      canUpdateLocationProgramDetails(
        pedagogicalYear?.id,
        EnrollmentTypeEnum.SubsequentEnrollment
      )
    );
  };

  const getAddress = (locationId: number) => {
    return locations
      .filter((l) => l.pedagogicalYearInstitutionId === locationId)
      .map((l) => l.address);
  };

  const getPhone = (locationId: number) => {
    return locations
      .filter((l) => l.pedagogicalYearInstitutionId === locationId)
      .map((l) => l.phone);
  };

  const getEmail = (locationId: number) => {
    return locations
      .filter((l) => l.pedagogicalYearInstitutionId === locationId)
      .map((l) => l.email);
  };

  const getLocationDescription = (locationId: number) => {
    return locations
      .filter((l) => l.pedagogicalYearInstitutionId === locationId)
      .map((l) => l.description);
  };

  const getProgramsSelectData = (locationId: number) => {
    const programs = locations
      .filter((l) => l.pedagogicalYearInstitutionId === locationId)
      .flatMap((l) => l.programs)
      .sort((a, b) => (a.code > b.code ? 1 : -1));

    return {
      valueProp: "pedagogicalYearInstitutionProgramId",
      textProp: "name",
      data:
        programs?.length > 0
          ? programs
          : [{ pedagogicalYearInstitutionProgramId: null, name: "" }],
    } as SelectData<LocationProgram>;
  };

  const isOnlyDetailsUpdateEnabled =
  !isPedagogicalYearInstitutionActive || 
    (id !== undefined &&
    !canUpdateLocationProgramData(
      pedagogicalYear?.id,
      EnrollmentTypeEnum.SubsequentEnrollment,
      locationProgramData?.monthId
    ) &&
    canUpdateLocationProgramDetails(
      pedagogicalYear?.id,
      EnrollmentTypeEnum.SubsequentEnrollment
    ));

  const handleStateChanged = (
    state: LocationProgramData,
    setState: (locationProgramData: LocationProgramData) => void
  ) => {
    const defaultLocationProgramId = 0;

    if (
      !state?.locationProgramId ||
      state.locationProgramId === defaultLocationProgramId
    ) {
      return;
    }

    const isSelectedProgramInLocation = locations
      .filter((l) => l.pedagogicalYearInstitutionId === state?.locationId)
      .flatMap((l) => l.programs)
      .some(
        (p) =>
          p.pedagogicalYearInstitutionProgramId === state?.locationProgramId
      );

    if (!isSelectedProgramInLocation) {
      const newState = {
        ...state,
        locationProgramId: defaultLocationProgramId,
      };
      setState(newState);
    }
  };

  const handleSave = async (
    isCreation: boolean,
    locationProgramData: LocationProgramData
  ) => {
    if (isCreation) {
      await createLocationProgramData(locationProgramData);
    } else if (isOnlyDetailsUpdateEnabled) {
      await updateLocationProgramDetails(locationProgramData);
    } else {
      await updateLocationProgramData(locationProgramData);
    }

    return true;
  };

  const type = id ? "update" : "create";

  return canEditOrUpdate() ? (
    <AdministrationCreateUpdate
      type={type}
      data={locationProgramData!}
      backUrl={subsequentCapacitiesRoute}
      backText={t("locationProgramDetails.createUpdateBackText")}
      title={t("locationProgramDetails.createUpdateTitle")}
      validationSchema={LocationProgramDataValidationSchema}
      onSave={handleSave}
      onStateChanged={handleStateChanged}
    >
      {(state, errors, onFieldChange) => (
        <>
          <Row className={styles.rowSpace}>
            <Col xs={12}>
              <Typography variant="baseBold" className={styles.subTitle}>
                {`${t("common.pedagogicalYear")} ${
                  pedagogicalYear?.name
                } ${"\xa0\xa0\xa0\xa0"} ${t("common.month")} ${state.month}`}
              </Typography>
            </Col>
            <Col xs={12}>
              <Autocomplete
                disabled={type === "update" || isOnlyDetailsUpdateEnabled}
                label={t("locationProgramDetails.location")}
                name={nameof<typeof state>("locationId")}
                value={state?.locationId}
                onChange={onFieldChange}
                selectData={
                  {
                    valueProp: "pedagogicalYearInstitutionId",
                    textProp: "name",
                    data: locations || [],
                  } as SelectData<Location>
                }
                errorText={errors?.locationId}
                required
              />
            </Col>
            <Col xs={12}>
              <TextField
                readonly
                label={`${t("locationProgramDetails.address")}`}
                value={getAddress(state.locationId)}
              />
            </Col>
            <Col md={6}>
              <TextField
                disabled={state.locationId == null || !isPedagogicalYearInstitutionActive}
                type="phone"
                label={t("common.phone")}
                value={
                  state.phone === undefined
                    ? getPhone(state.locationId)
                    : state.phone
                }
                name={nameof<typeof state>("phone")}
                onChange={onFieldChange}
              />
            </Col>
            <Col md={6}>
              <TextField
                disabled={state.locationId == null || !isPedagogicalYearInstitutionActive}
                type="email"
                label={t("locationProgramDetails.email")}
                value={
                  state.email === undefined
                    ? getEmail(state.locationId)
                    : state.email
                }
                name={nameof<typeof state>("email")}
                onChange={onFieldChange}
              />
            </Col>
            <Col md={12}>
              <TextArea
                disabled={state.locationId == null || !isPedagogicalYearInstitutionActive}
                label={t("locationProgramDetails.locationDescription")}
                ariaLabel={t("locationProgramDetails.locationDescription")}
                value={
                  state.locationDescription === undefined
                    ? getLocationDescription(state.locationId)
                    : state.locationDescription
                }
                maxLength={700}
                rows={8}
                name={nameof<typeof state>("locationDescription")}
                onChange={onFieldChange}
              />
            </Col>
            <Col xs={12}>
              <Autocomplete
                disabled={type === "update" || isOnlyDetailsUpdateEnabled}
                label={t("locationProgramDetails.program")}
                name={nameof<typeof state>("locationProgramId")}
                value={state?.locationProgramId}
                onChange={onFieldChange}
                selectData={getProgramsSelectData(state.locationId)}
                errorText={errors?.locationProgramId}
                required
              />
            </Col>
            <Col xs={12}>
              <TextArea
                disabled={isOnlyDetailsUpdateEnabled}
                name={nameof<typeof state>("programDescription")}
                value={state?.programDescription ?? ""}
                label={t("locationProgramDetails.programDescription")}
                ariaLabel={t("locationProgramDetails.programDescription")}
                maxLength={700}
                rows={8}
                onChange={onFieldChange}
                errorText={errors?.programDescription}
              />
            </Col>
            <Col xs={12}>
              <Select
                disabled={isOnlyDetailsUpdateEnabled}
                label={t("locationProgramDetails.workOrganization")}
                name={nameof<typeof state>("workOrganizationId")}
                value={state?.workOrganizationId}
                onChange={onFieldChange}
                selectData={
                  {
                    valueProp: "id",
                    textProp: "name",
                    data: workOrganizations || [],
                  } as SelectData<CodeBook>
                }
                errorText={errors?.workOrganizationId}
                required
              />
            </Col>
            <Col md={3}>
              <TextField
                type="number"
                label={`${t("locationProgramDetails.capacity")}`}
                name={nameof<typeof state>("capacity")}
                value={state?.capacity}
                min={0}
                max={1000}
                onChange={onFieldChange}
                errorText={errors?.capacity}
                required
              />
            </Col>
            <Col md={3}>
              <TextField
                disabled={isOnlyDetailsUpdateEnabled}
                type="number"
                label={`${t("locationProgramDetails.duration")}`}
                name={nameof<typeof state>("duration")}
                value={state?.duration}
                onChange={onFieldChange}
                errorText={errors?.duration}
                min={0}
                max={18}
                required
              />
            </Col>
            <Col md={6}>
            <TextField
                disabled={isOnlyDetailsUpdateEnabled}
                type={isOnlyDetailsUpdateEnabled ? "text" : "number"}
                label={isOnlyDetailsUpdateEnabled ? `${t("locationProgramDetails.priceEURandHRK")}` : `${t("locationProgramDetails.priceEUR")}`}
                name={nameof<typeof state>("price")}
                value={isOnlyDetailsUpdateEnabled ? `${state?.price?.toFixed(2)}/${(state?.price * exchangeRate)?.toFixed(2)}` : state?.price}
                onChange={onFieldChange}
                errorText={errors?.price}
                required
              />
            </Col>
          </Row>

          <Row>
            <Col md={5}>
              <TimePicker
                disabled={isOnlyDetailsUpdateEnabled || !state.duration}
                value={state?.firstShiftFrom}
                label={t("locationProgramDetails.firstShiftFrom")}
                name={nameof<typeof state>("firstShiftFrom")}
                onChange={onFieldChange}
              />
            </Col>
            <Col md={5}>
              <TextField
                disabled
                label={t("locationProgramDetails.firstShiftTo")}
                value={
                  state.duration
                    ? hrShortTime(
                        new Date(
                          1970,
                          1,
                          1,
                          state.firstShiftFrom.getHours() + state.duration,
                          state.firstShiftFrom.getMinutes() +
                            decimalToMinutes(
                              state.duration?.toLocaleString()?.split(".")[1]
                            )
                        )
                      )
                    : ""
                }
              />
            </Col>
          </Row>

          {state?.workOrganizationId === WorkOrganizationEnum.Shifts && (
            <>
              <Row>
                <Col md={5}>
                  <TimePicker
                    disabled={isOnlyDetailsUpdateEnabled || !state.duration}
                    value={state?.secondShiftFrom}
                    label={t("locationProgramDetails.secondShiftFrom")}
                    name={nameof<typeof state>("secondShiftFrom")}
                    onChange={onFieldChange}
                  />
                </Col>
                <Col md={5}>
                  <TextField
                    disabled
                    label={t("locationProgramDetails.secondShiftTo")}
                    value={
                      state.duration
                        ? hrShortTime(
                            new Date(
                              1970,
                              1,
                              1,
                              state.secondShiftFrom.getHours() + state.duration,
                              state.secondShiftFrom.getMinutes() +
                                decimalToMinutes(
                                  state.duration
                                    ?.toLocaleString()
                                    ?.split(".")[1]
                                )
                            )
                          )
                        : ""
                    }
                  />
                </Col>
              </Row>
            </>
          )}
          <Row>
            <Col md={12}>
              <TextArea
                disabled={isOnlyDetailsUpdateEnabled}
                name={nameof<typeof state>("note")}
                value={state?.note}
                label={t("locationProgramDetails.note")}
                ariaLabel={t("locationProgramDetails.note")}
                maxLength={200}
                rows={4}
                onChange={onFieldChange}
                errorText={errors?.note}
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
            <InfoPanel
                message={t("locationProgramDetails.exchangeRate", {
                  exchangeRate: exchangeRate
              })}
                color="warning"
                fullWidth
              />
            </Col>
          </Row>
        </>
      )}
    </AdministrationCreateUpdate>
  ) : (
    <AdministrationCreateUpdate
      type="read"
      data={locationProgramData!}
      backUrl={subsequentCapacitiesRoute}
      backText={t("locationProgramDetails.createUpdateBackText")}
      title={t("locationProgramDetails.createUpdateTitle")}
      validationSchema={LocationProgramDataValidationSchema}
    >
      {(state, errors, onFieldChange) => (
        <>
          <Row className={styles.rowSpace}>
            <Col xs={12}>
              <Typography variant="baseBold" className={styles.subTitle}>
                {`${t("common.pedagogicalYear")} ${
                  pedagogicalYear?.name
                } ${"\xa0\xa0\xa0\xa0"} ${t("common.month")} ${state.month}`}
              </Typography>
            </Col>
            <Col xs={12}>
              <Autocomplete
                disabled
                label={t("locationProgramDetails.location")}
                name={nameof<typeof state>("locationId")}
                value={state?.locationId}
                onChange={onFieldChange}
                selectData={
                  {
                    valueProp: "pedagogicalYearInstitutionId",
                    textProp: "name",
                    data: locations || [],
                  } as SelectData<Location>
                }
              />
            </Col>
            <Col xs={12}>
              <TextField
                readonly
                label={`${t("locationProgramDetails.address")}`}
                value={getAddress(state.locationId)}
                disabled
              />
            </Col>
            <Col md={6}>
              <TextField
                disabled
                type="phone"
                label={t("common.phone")}
                value={
                  state.phone === undefined
                    ? getPhone(state.locationId)
                    : state.phone
                }
                name={nameof<typeof state>("phone")}
              />
            </Col>
            <Col md={6}>
              <TextField
                disabled
                type="email"
                label={t("locationProgramDetails.email")}
                value={
                  state.email === undefined
                    ? getEmail(state.locationId)
                    : state.email
                }
                name={nameof<typeof state>("email")}
              />
            </Col>
            <Col md={12}>
              <TextArea
                disabled
                label={t("locationProgramDetails.locationDescription")}
                ariaLabel={t("locationProgramDetails.locationDescription")}
                value={
                  state.locationDescription === undefined
                    ? getLocationDescription(state.locationId)
                    : state.locationDescription
                }
                maxLength={700}
                rows={8}
                name={nameof<typeof state>("locationDescription")}
              />
            </Col>
            <Col xs={12}>
              <Autocomplete
                disabled
                label={t("locationProgramDetails.program")}
                name={nameof<typeof state>("locationProgramId")}
                value={state?.locationProgramId}
                onChange={onFieldChange}
                selectData={getProgramsSelectData(state.locationId)}
              />
            </Col>
            <Col xs={12}>
              <TextArea
                disabled
                name={nameof<typeof state>("programDescription")}
                value={state?.programDescription ?? ""}
                label={t("locationProgramDetails.programDescription")}
                ariaLabel={t("locationProgramDetails.programDescription")}
                maxLength={700}
                rows={8}
              />
            </Col>
            <Col xs={12}>
              <Select
                disabled
                label={t("locationProgramDetails.workOrganization")}
                name={nameof<typeof state>("workOrganizationId")}
                value={state?.workOrganizationId}
                onChange={onFieldChange}
                selectData={
                  {
                    valueProp: "id",
                    textProp: "name",
                    data: workOrganizations || [],
                  } as SelectData<CodeBook>
                }
              />
            </Col>
            <Col md={3}>
              <TextField
                disabled
                type="number"
                label={`${t("locationProgramDetails.capacity")}`}
                name={nameof<typeof state>("capacity")}
                value={state?.capacity}
              />
            </Col>
            <Col md={3}>
              <TextField
                disabled
                type="number"
                label={`${t("locationProgramDetails.duration")}`}
                name={nameof<typeof state>("duration")}
                value={state?.duration}
              />
            </Col>
            <Col md={6}>
            <TextField
                disabled
                type="text"
                label={`${t("locationProgramDetails.priceEURandHRK")}`}
                name={nameof<typeof state>("price")}
                value={`${state?.price?.toFixed(2)}/${(state?.price * exchangeRate)?.toFixed(2)}`}
              />
            </Col>
          </Row>

          <Row>
            <Col md={5}>
              <TimePicker
                disabled
                value={state?.firstShiftFrom}
                label={t("locationProgramDetails.firstShiftFrom")}
                name={nameof<typeof state>("firstShiftFrom")}
              />
            </Col>
            <Col md={5}>
              <TextField
                disabled
                label={t("locationProgramDetails.firstShiftTo")}
                value={
                  state.duration
                    ? hrShortTime(
                        new Date(
                          1970,
                          1,
                          1,
                          state.firstShiftFrom.getHours() + state.duration,
                          state.firstShiftFrom.getMinutes() +
                            decimalToMinutes(
                              state.duration?.toLocaleString()?.split(".")[1]
                            )
                        )
                      )
                    : ""
                }
              />
            </Col>
          </Row>

          {state?.workOrganizationId === WorkOrganizationEnum.Shifts && (
            <>
              <Row>
                <Col md={5}>
                  <TimePicker
                    disabled
                    value={state?.secondShiftFrom}
                    label={t("locationProgramDetails.secondShiftFrom")}
                    name={nameof<typeof state>("secondShiftFrom")}
                  />
                </Col>
                <Col md={5}>
                  <TextField
                    disabled
                    label={t("locationProgramDetails.secondShiftTo")}
                    value={
                      state.duration
                        ? hrShortTime(
                            new Date(
                              1970,
                              1,
                              1,
                              state.secondShiftFrom.getHours() + state.duration,
                              state.secondShiftFrom.getMinutes() +
                                decimalToMinutes(
                                  state.duration
                                    ?.toLocaleString()
                                    ?.split(".")[1]
                                )
                            )
                          )
                        : ""
                    }
                  />
                </Col>
              </Row>
            </>
          )}
          <Row>
            <Col md={12}>
              <TextArea
                disabled
                readonly
                name={nameof<typeof state>("note")}
                value={state?.note}
                label={t("locationProgramDetails.note")}
                ariaLabel={t("locationProgramDetails.note")}
                maxLength={200}
                rows={4}
              />
            </Col>
          </Row>
        </>
      )}
    </AdministrationCreateUpdate>
  );
};
